import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getMessageTemplates,
  createMessageTemplate,
  updateMessageTemplate,
  deleteMessageTemplate,
  getTemplateVariables,
  previewMessageTemplate
} from '../utils/api.js'
import { useFetch } from '../hooks/useFetch.js'
import { hapticImpact, hapticNotification, showConfirm } from '../utils/telegram.js'

const EMPTY_TEMPLATE = {
  templateName: '',
  templateBody: '',
  venueId: ''
}

function VariableChip({ variable, onInsert }) {
  return (
    <button
      type="button"
      className="badge"
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title={variable.description}
      onClick={() => onInsert(variable.key)}
    >
      {variable.key}
    </button>
  )
}

function MessageTemplateForm({ initial, variables, onSave, onCancel }) {
  const [form, setForm] = useState(initial ? {
    templateName: initial.template_name,
    templateBody: initial.template_body,
    venueId: initial.venue_id || ''
  } : EMPTY_TEMPLATE)
  const [preview, setPreview] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [textareaRef, setTextareaRef] = useState(null)

  const insertVariable = (key) => {
    if (!textareaRef) {
      setForm(f => ({ ...f, templateBody: f.templateBody + key }))
      return
    }
    const start = textareaRef.selectionStart
    const end = textareaRef.selectionEnd
    const body = form.templateBody
    const newBody = body.substring(0, start) + key + body.substring(end)
    setForm(f => ({ ...f, templateBody: newBody }))
    setTimeout(() => {
      textareaRef.focus()
      textareaRef.setSelectionRange(start + key.length, start + key.length)
    }, 0)
  }

  const handlePreview = async () => {
    setPreviewing(true)
    try {
      const res = await previewMessageTemplate(form.templateBody)
      setPreview(res.data.preview)
    } catch {
      setPreview('(preview failed)')
    } finally {
      setPreviewing(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    hapticImpact('light')
    try {
      await onSave(form)
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label">Template Name *</label>
        <input
          className="form-input"
          value={form.templateName}
          onChange={e => setForm(f => ({ ...f, templateName: e.target.value }))}
          placeholder="e.g. Quiz Night Update"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Message Body *</label>
        <p style={{ fontSize: 12, color: 'var(--tg-theme-hint-color)', marginBottom: 6 }}>
          Tap a variable below to insert it at cursor position. Supports Telegram Markdown.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          {variables.map(v => (
            <VariableChip key={v.key} variable={v} onInsert={insertVariable} />
          ))}
        </div>
        <textarea
          ref={setTextareaRef}
          className="form-textarea"
          value={form.templateBody}
          onChange={e => setForm(f => ({ ...f, templateBody: e.target.value }))}
          placeholder="🔔 Update at *{{venue_name}}*!&#10;📅 {{start_time}}&#10;🪑 {{available_slots}}/{{total_slots}} slots"
          required
          rows={6}
        />
      </div>

      {form.templateBody && (
        <div className="form-group">
          <button type="button" className="btn btn-secondary btn-sm" onClick={handlePreview} disabled={previewing}>
            {previewing ? 'Loading...' : '👁 Preview'}
          </button>
          {preview && (
            <div className="card" style={{ marginTop: 10, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13 }}>
              {preview}
            </div>
          )}
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Venue ID (optional)</label>
        <input
          className="form-input"
          value={form.venueId}
          onChange={e => setForm(f => ({ ...f, venueId: e.target.value }))}
          placeholder="Leave empty to use for all venues"
        />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Template'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

export default function MessageTemplatesPage() {
  const navigate = useNavigate()
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [alert, setAlert] = useState(null)

  const { data, loading, error, refetch } = useFetch(() => getMessageTemplates())
  const { data: variablesData } = useFetch(() => getTemplateVariables())

  const templates = data?.data || []
  const variables = variablesData?.variables || []

  const handleSave = async (form) => {
    if (editingTemplate) {
      await updateMessageTemplate(editingTemplate.id, form)
      setAlert({ type: 'success', text: 'Template updated!' })
    } else {
      await createMessageTemplate(form)
      setAlert({ type: 'success', text: 'Template created!' })
    }
    hapticNotification('success')
    setShowForm(false)
    setEditingTemplate(null)
    refetch()
  }

  const handleDelete = async (tmpl) => {
    const confirmed = await showConfirm(`Delete "${tmpl.template_name}"?`)
    if (!confirmed) return
    hapticImpact('medium')
    try {
      await deleteMessageTemplate(tmpl.id)
      setAlert({ type: 'success', text: 'Template deleted' })
      refetch()
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Delete failed' })
    }
  }

  return (
    <div>
      <button
        className="btn btn-secondary btn-sm"
        style={{ marginTop: 16, marginBottom: 8, width: 'auto' }}
        onClick={() => navigate('/settings')}
      >
        ← Back
      </button>

      <h2 className="section-title">Message Templates</h2>

      <p style={{ fontSize: 13, color: 'var(--tg-theme-hint-color)', marginBottom: 16 }}>
        Customize the notification message sent to your chat when venue updates occur. Uses variables to insert live data.
      </p>

      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.text}
          <button style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setAlert(null)}>✕</button>
        </div>
      )}

      {!showForm && (
        <button
          className="btn btn-primary"
          style={{ marginBottom: 16 }}
          onClick={() => { setEditingTemplate(null); setShowForm(true) }}
        >
          + Create Message Template
        </button>
      )}

      {showForm && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>{editingTemplate ? 'Edit Template' : 'New Message Template'}</h3>
          <MessageTemplateForm
            initial={editingTemplate}
            variables={variables}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingTemplate(null) }}
          />
        </div>
      )}

      {loading && <div className="status-message"><div className="loading-spinner" /></div>}
      {error && <div className="alert alert-error">{error}</div>}

      {!loading && templates.length === 0 && !showForm && (
        <div className="empty-state">
          <div className="empty-state-icon">✉️</div>
          <div className="empty-state-title">No message templates yet</div>
          <div className="empty-state-text">Create a template to control how update notifications look in your chat.</div>
        </div>
      )}

      {templates.map(tmpl => (
        <div key={tmpl.id} className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div className="card-title">{tmpl.template_name}</div>
              <div className="card-subtitle" style={{ marginTop: 6, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                {tmpl.template_body.length > 120 ? tmpl.template_body.substring(0, 120) + '…' : tmpl.template_body}
              </div>
              {tmpl.venue_id && (
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--tg-theme-hint-color)' }}>
                  Venue: {tmpl.venue_id}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 12 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setEditingTemplate(tmpl); setShowForm(true) }}
              >
                Edit
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleDelete(tmpl)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
